import { useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Bot, LayoutDashboard, Menu, Users, Wallet, type LucideProps } from 'lucide-react';
import { haptic } from '@/lib/telegram';
import { queryClient } from '@/lib/query-client';
import { api } from '@/lib/api';
import { canOpenPath, limitedAccess } from '@/lib/access';

/**
 * Нижняя панель кабинета: плавающая стеклянная таблетка (стили .glass и .tab-pill в
 * index.css). Пять разделов, как в кабинете на сайте: Сводка, Клиенты, Бот, Деньги, Ещё.
 * До 17.10.2026 вкладки были Главная, Боты, Стата, Финансы, Профиль, а активная
 * подсвечивалась синим. Теперь активную отмечает одна стеклянная капля: она переезжает
 * к выбранной вкладке, а не вспыхивает на новом месте.
 *
 * Раздел узнаёт свои внутренние экраны по первому сегменту адреса: «Бот» горит и на
 * тарифах, и на текстах бота. Всё, что не распознано, относится к «Ещё».
 */
type Tab = {
  to: '/dashboard' | '/clients' | '/bot' | '/finance' | '/more';
  label: string;
  Icon: ComponentType<LucideProps>;
  sections: string[];
};

const TABS: Tab[] = [
  { to: '/dashboard', label: 'Tab summary', Icon: LayoutDashboard, sections: ['dashboard'] },
  { to: '/clients', label: 'Tab clients', Icon: Users, sections: ['clients', 'subscriptions', 'bot-reviews'] },
  {
    to: '/bot',
    label: 'Tab bot',
    Icon: Bot,
    sections: ['bot', 'bots', 'pricing', 'bot-texts', 'bot-warmup', 'warmup-rules', 'onboarding'],
  },
  { to: '/finance', label: 'Tab money', Icon: Wallet, sections: ['finance', 'payouts', 'client-withdrawals', 'stats'] },
  { to: '/more', label: 'Tab more', Icon: Menu, sections: [] },
];

export function activeTabIndex(pathname: string, tabs: Tab[] = TABS): number {
  const section = pathname.split('/')[1] ?? '';
  const i = tabs.findIndex((t) => t.sections.includes(section));
  return i === -1 ? tabs.length - 1 : i;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Радиус концов капли: половина высоты вкладки (h-12). */
const CAP = 24;

export function TabBar({ pathname, hidden }: { pathname: string; hidden: boolean }) {
  const { t } = useTranslation();
  // Менеджеру и «своему набору» показываем только вкладки, куда его пустит бэк.
  const { data: me } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const access = limitedAccess(me);
  const tabs = access === null ? TABS : TABS.filter((tab) => canOpenPath(access, tab.to));
  const routeActive = activeTabIndex(pathname, tabs);
  // Капля едет к вкладке сразу по нажатию, не дожидаясь, пока отрисуется новый раздел:
  // иначе она стояла, пока грузился и рисовался экран, и трогалась с опозданием.
  const [tapped, setTapped] = useState<number | null>(null);
  const active = tapped ?? routeActive;
  useLayoutEffect(() => setTapped(null), [routeActive]);
  const items = useRef<(HTMLAnchorElement | null)[]>([]);
  const [pill, setPill] = useState<{ x: number; w: number } | null>(null);
  // Первую позицию капля занимает без анимации: иначе при открытии кабинета она
  // прилетала бы из левого угла.
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const el = items.current[active];
    if (!el) return;
    const measure = () => setPill({ x: el.offsetLeft, w: el.offsetWidth });
    measure();
    // Подпись активной вкладки меняет её ширину, шрифт может догрузиться позже.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const id = requestAnimationFrame(() => setReady(true));
    return () => {
      ro.disconnect();
      cancelAnimationFrame(id);
    };
  }, [active]);

  return (
    <nav
      aria-hidden={hidden}
      className={`glass tab-bar fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-0.5 p-1.5 rounded-full transform-gpu transition-opacity duration-150 ${
        hidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
    >
      {/* Капля из трёх частей: два круглых конца и середина, растянутая scaleX. Всё
          движение только transform, его ведёт компоновщик, и капля не дёргается, пока
          главный поток занят новым разделом. Раньше анимировалась ширина: это работа
          главного потока, и на слабом телефоне капля шла рывками. */}
      {pill && (
        <span aria-hidden="true" className={`tab-pill ${ready ? 'is-ready' : ''}`}>
          <i className="tab-pill-cap is-left" style={{ transform: `translateX(${pill.x}px)` }} />
          <i
            className="tab-pill-mid"
            style={{
              // Встык, без нахлёста: полупрозрачные части на нахлёсте дали бы светлую черту.
              // offsetLeft целый, поэтому щели на дробном пикселе тоже нет.
              transform: `translateX(${pill.x + CAP}px) scaleX(${Math.max(0, pill.w - 2 * CAP)})`,
            }}
          />
          <i className="tab-pill-cap is-right" style={{ transform: `translateX(${pill.x + pill.w - CAP}px)` }} />
        </span>
      )}
      {tabs.map((tab, i) => {
        const isActive = i === active;
        const label = t(tab.label);
        return (
          <Link
            key={tab.to}
            ref={(el) => {
              items.current[i] = el;
            }}
            to={tab.to}
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            preload="intent"
            // Без viewTransition: на время перехода браузер показывал застывший снимок
            // страницы вместе с панелью, капля стояла и потом прыгала на середину пути.
            onPointerDown={() => {
              if (!isActive) setTapped(i);
            }}
            onClick={(e) => {
              haptic('light');
              // Повторный тап по активной вкладке: наверх и свежие данные.
              if (isActive && pathname === tab.to) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
                void queryClient.refetchQueries({ type: 'active' });
              }
            }}
            className={`relative z-[1] flex h-12 items-center justify-center gap-[7px] rounded-full transition-colors duration-100 active:scale-95 ${
              isActive ? 'px-[17px] text-white' : 'px-[13px] text-white/[0.58] active:text-white'
            }`}
          >
            <tab.Icon size={22} strokeWidth={1.9} />
            {/* Подпись только у активной и только от 360px: на 320px панель с подписью
                вылезала за края экрана. */}
            {isActive && <span className="hidden text-sm font-semibold whitespace-nowrap min-[360px]:inline">{label}</span>}
          </Link>
        );
      })}
    </nav>
  );
}
